import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import MyNavbar from "./navbar";
import { formatPrice } from "../convertPrice";
import MailDeliveryNote from "./MailDeliveryNote";

// Shown after Stripe redirects back. Fulfilment happens server side, and the
// webhook may land a moment before or after the browser does, so this polls
// briefly rather than declaring an order missing on the first try.
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

const OrderConfirmation = () => {
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | timeout | error
  const [attempt, setAttempt] = useState(0);

  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const load = useCallback(async () => {
    if (!sessionId) {
      setState("error");
      return;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_HOST}${process.env.REACT_APP_PORT}/order-summary/${sessionId}`
      );
      if (res.status === 200 && res.data.status === "complete") {
        setSummary(res.data);
        setState("ready");
        // The cart is cleared server side; drop the guest copy too.
        localStorage.removeItem("guest_products");
        localStorage.removeItem("guest_user");
      } else {
        setAttempt((a) => a + 1);
      }
    } catch (err) {
      console.error("Could not load order summary:", err.message);
      setAttempt((a) => a + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (state !== "loading") return;
    if (attempt === 0) {
      load();
      return;
    }
    if (attempt >= MAX_ATTEMPTS) {
      setState("timeout");
      return;
    }
    const t = setTimeout(load, POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [attempt, state, load]);

  const currency = summary?.currency;

  return (
    <div className="fullscreen">
      <MyNavbar />
      <main className="container py-5" style={{ maxWidth: "720px" }}>
        {state === "loading" && (
          <div className="text-center py-5">
            <div className="spinner-border text-secondary mb-3" role="status">
              <span className="visually-hidden">Loading</span>
            </div>
            <h4 className="mb-1">Confirming your order</h4>
            <p className="text-secondary mb-0">
              Your payment went through. We are just recording the order.
            </p>
          </div>
        )}

        {state === "ready" && summary && (
          <>
            <div className="text-center mb-4">
              <i
                className="bi bi-check-circle-fill text-success"
                style={{ fontSize: "44px" }}
              ></i>
              <h3 className="mt-2 mb-1">Thank you{summary.buyerName ? `, ${summary.buyerName}` : ""}</h3>
              <p className="text-secondary mb-0">
                Your order is confirmed. A receipt is on its way to{" "}
                <b>{summary.buyerEmail}</b>.
              </p>
            </div>

            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between flex-wrap mb-3">
                <div>
                  <div className="text-secondary small text-uppercase">Order number</div>
                  <div className="fw-semibold">{summary.items[0]?.orderId}</div>
                </div>
                <div className="text-md-end">
                  <div className="text-secondary small text-uppercase">Placed on</div>
                  <div className="fw-semibold">
                    {summary.placedOn ? String(summary.placedOn).slice(0, 10) : "-"}
                  </div>
                </div>
              </div>

              {summary.items.map((item, i) => (
                <div
                  key={i}
                  className="d-flex align-items-center gap-3 py-2 border-top"
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt=""
                      width="56"
                      height="56"
                      style={{ objectFit: "cover" }}
                      className="rounded"
                    />
                  )}
                  <div className="flex-grow-1">
                    <div>{item.name}</div>
                    <div className="text-secondary small">
                      Qty {item.quantity} &middot; {item.status}
                    </div>
                  </div>
                  <div className="fw-semibold">
                    {currency === "INR" ? "₹" : "$"}{" "}
                    {formatPrice(item.amount, currency)}
                  </div>
                </div>
              ))}

              <div className="d-flex justify-content-between border-top pt-3 mt-2">
                <span className="fw-bold">Total paid</span>
                <span className="fw-bold">
                  {currency === "INR" ? "₹" : "$"}{" "}
                  {formatPrice(summary.total, currency)}
                </span>
              </div>
            </div>

            <p className="text-secondary small">
              We will email you again when your order ships. All sales are final
              &mdash; no exchanges or returns.
            </p>

            <MailDeliveryNote className="mb-3" />

            <div className="d-flex gap-2 flex-wrap">
              <Link to="/orders" className="btn btn-dark px-4">
                View my orders
              </Link>
              <Link to="/" className="btn btn-outline-dark px-4">
                Continue shopping
              </Link>
            </div>
          </>
        )}

        {state === "timeout" && (
          <div className="text-center py-5">
            <i
              className="bi bi-clock-history text-secondary"
              style={{ fontSize: "40px" }}
            ></i>
            <h4 className="mt-2 mb-1">Your payment went through</h4>
            <p className="text-secondary">
              We are still recording the order. It will appear in your orders
              shortly, and the receipt will arrive by email. Nothing further is
              needed from you.
            </p>
            <Link to="/orders" className="btn btn-dark px-4 mt-2">
              View my orders
            </Link>
          </div>
        )}

        {state === "error" && (
          <div className="text-center py-5">
            <h4 className="mb-1">We could not find that order</h4>
            <p className="text-secondary">
              The link may be incomplete. If you were charged, your order is
              safe and the receipt will arrive by email.
            </p>
            <Link to="/" className="btn btn-dark px-4 mt-2">
              Back to the store
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default OrderConfirmation;
